import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { fosApi, type FosContext } from '../lib/fosApi';

/**
 * The caller's Friends of Scouting standing, from the server. One request per tenant is shared by everything on screen (the
 * sidebar entry and the FOS pages), and refreshed after something that could change it (turning FOS on).
 */
const cache = new Map<string, Promise<FosContext>>();

export function useFosContext() {
  const { profile, tenantIds } = useAuth();
  const tenantId = profile ? (tenantIds[0] ?? null) : null;
  const [state, setState] = useState<{ context: FosContext | null; loading: boolean; error: string }>({ context: null, loading: true, error: '' });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!tenantId) { setState({ context: null, loading: false, error: '' }); return () => { cancelled = true; }; }
    let promise = cache.get(tenantId);
    if (!promise) { promise = fosApi.context({}); cache.set(tenantId, promise); promise.catch(() => cache.delete(tenantId)); }
    setState((s) => ({ ...s, loading: true }));
    void promise.then((context) => { if (!cancelled) setState({ context, loading: false, error: '' }); })
      .catch((cause) => { if (!cancelled) setState({ context: null, loading: false, error: cause instanceof Error ? cause.message : 'Unable to load Friends of Scouting.' }); });
    return () => { cancelled = true; };
  }, [tenantId, tick]);

  const refresh = useCallback(() => { if (tenantId) cache.delete(tenantId); setTick((n) => n + 1); }, [tenantId]);
  return { ...state, refresh };
}
